import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { TextingAutomationStepReference } from '../../../../extViewmodels';
import { useEventLogging } from '../../../../models/Logging';
import { getPlainTextPreviewForRawRichTextContent } from '../../../../models/UiUtils';
import { useErrorMessages, useFullscreenModal } from '../../../../models/hooks/appStateHooks';
import { useDeleteAutomationTemplateMutation } from '../../../../queries';
import {
	useAutomationTemplateFilterInfiniteQuery,
	invalidateAutomationTemplateFilter,
} from '../../../../queries/AutomationTemplate/useAutomationTemplateFilterInfiniteQuery';
import { AutomationTemplateFilterProperty } from '../../../../extViewmodels/sdk/models/Automation';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../components/ConfirmationDialog';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { OutboundTextMessageBubble } from '../../../components/texting/MessageBubble/OutboundTextMessageBubble';
import { brandPrimary } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { styleSheet } from './styles';
import { DraftErrorBoundary } from '../../../components/ErrorBoundary/DraftErrorBoundary';
import { DraftCardSkeletonGrid } from '../../../components/LoadingStates/DraftCardSkeleton';

// Type guard to safely access draft steps
const getDraftSteps = (template: Api.IAutomationTemplateReference): Api.IAutomationStepReference[] | undefined => {
	if (template.draftSteps) {
		return template.draftSteps;
	}
	// Handle legacy draft structure safely
	const legacyDraft = template as {
		draft?: { steps?: Api.IAutomationStepReference[] };
	};
	return legacyDraft.draft?.steps;
};

// Type guard for texting automation steps
const isTextingAutomationStep = (step: Api.IAutomationStepReference): step is TextingAutomationStepReference => {
	return step._type === 'TextingAutomationStep';
};

export const TextingCampaignDraftBrowserCards = React.memo<{
	enabled?: boolean;
}>(({ enabled = true }) => {
	const { logApiError } = useEventLogging('TextingCampaignDraftBrowserCards');
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();
	const [draftToDelete, setDraftToDelete] = React.useState<Api.IAutomationTemplateReference>(null);

	// Memoized filter for draft texting campaigns
	const draftFilter = React.useMemo(
		(): Api.AutomationTemplateFilter => ({
			criteria: [
				{
					property: AutomationTemplateFilterProperty.DraftSingleStep,
					value: true,
				},
				{
					property: AutomationTemplateFilterProperty.DraftTriggerType,
					value: 'TextingCampaignAutomationTrigger',
				},
				{
					property: AutomationTemplateFilterProperty.Status,
					value: 'Archived',
					op: Api.FilterOperator.Not,
				},
			],
			op: Api.FilterOperator.And,
		}),
		[]
	);

	const draftsQuery = useAutomationTemplateFilterInfiniteQuery({
		filter: draftFilter,
		enabled,
	});

	const deleteAutomationTemplateMutation = useDeleteAutomationTemplateMutation({
		onError: error => {
			logApiError('DeleteTextingCampaignDraft-Error', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			invalidateAutomationTemplateFilter();
			setDraftToDelete(null);
		},
	});

	const deleteDraft = React.useCallback((template: Api.IAutomationTemplateReference) => {
		setDraftToDelete(template);
	}, []);

	const editDraft = React.useCallback(
		(template: Api.IAutomationTemplateReference) => {
			fullscreenModal.history.push(`/texting-campaign/draft/edit/${template.id}`);
		},
		[fullscreenModal.history]
	);

	const onDeleteConfirmationRequestClose = React.useCallback(
		(result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
			if (result?.isDestructive && !cancel && draftToDelete) {
				deleteAutomationTemplateMutation.mutate({ id: draftToDelete.id });
			} else {
				setDraftToDelete(null);
			}
		},
		[draftToDelete, deleteAutomationTemplateMutation]
	);

	if (draftsQuery.status === 'error') {
		return (
			<DraftErrorBoundary onRetry={() => draftsQuery.refetch()}>
				<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.wFull, bs.pt8)}>
					<div className={css(bs.textHeader, bs.textLg, bs.mb2)}>Unable to load drafts</div>
					<div className={css(bs.textLabel, bs.textSm, bs.textCenter, bs.mb4)}>
						There was an error loading your drafts. Please try again.
					</div>
					<button className={css(bs.ctaButtonReverseSmall)} onClick={() => draftsQuery.refetch()}>
						Retry
					</button>
				</div>
			</DraftErrorBoundary>
		);
	}

	if (draftsQuery.status !== 'success' || !draftsQuery.data) {
		return (
			<DraftErrorBoundary>
				<DraftCardSkeletonGrid count={6} data-testid='draft-card-skeleton' />
			</DraftErrorBoundary>
		);
	}

	if (draftsQuery.data?.pages?.every(page => page?.values?.length === 0)) {
		return (
			<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.wFull, bs.pt8)}>
				<div className={css(bs.textHeader, bs.textLg, bs.mb2)}>No drafts yet</div>
				<div className={css(bs.textLabel, bs.textSm, bs.textCenter)}>
					Start creating a texting campaign and save it as a draft to see it here.
				</div>
			</div>
		);
	}

	return (
		<DraftErrorBoundary onRetry={() => draftsQuery.refetch()}>
			<div className={css(styleSheet.cardList)}>
				{draftsQuery.data?.pages?.map((page, _pageIndex) => {
					if (!page?.values || !Array.isArray(page.values)) {
						return null;
					}
					return page.values
						.filter(template => {
							if (!template || typeof template !== 'object') {
								return false;
							}

							try {
								const draftSteps = getDraftSteps(template);
								return draftSteps && draftSteps.length > 0 && isTextingAutomationStep(draftSteps[0]);
							} catch {
								return false;
							}
						})
						.map(template => {
							try {
								const draftSteps = getDraftSteps(template);
								if (!draftSteps || !Array.isArray(draftSteps) || draftSteps.length === 0) {
									return null;
								}

								const step = draftSteps[0];
								if (!isTextingAutomationStep(step)) {
									return null;
								}

								let text = '';
								try {
									if (step?.content && typeof step.content === 'object') {
										const result = getPlainTextPreviewForRawRichTextContent(step.content, { maxCharCount: 80 });
										text = result?.text?.trim() || '';
									}
								} catch (error) {
									console.warn('Failed to extract draft preview text:', error);
								}

								// Provide fallback text for better UX
								if (!text) {
									text = !step?.content ? 'Draft message (no content yet)' : 'Draft message';
								}
								const lastModified = template.lastModifiedDate
									? new Date(template.lastModifiedDate).toLocaleDateString()
									: '';

								return (
									<div
										key={step.id}
										className={css(styleSheet.card, bs.shadow)}
										role='article'
										aria-label={`Draft campaign: ${template.name}`}
									>
										<h3 className={css(styleSheet.cardName)} title={template.name}>
											{template.name}
										</h3>

										<Dropdown>
											<DropdownEllipsisTrigger
												styles={[styleSheet.moreMenu]}
												aria-label={`Actions for ${template.name}`}
											/>
											<DropdownContent>
												<DropdownItem onClick={() => editDraft(template)}>Edit Draft</DropdownItem>
												<DropdownItem onClick={() => deleteDraft(template)}>Delete Draft</DropdownItem>
											</DropdownContent>
										</Dropdown>

										<div role='region' aria-label='Message preview' className={css(bs.wFull)}>
											<OutboundTextMessageBubble
												backgroundColor={brandPrimary}
												styleDeclarations={[bs.flex1, bs.wFull, bs.boxBorder, bs.block]}
											>
												<div className={css(styleSheet.messagePreviewText)}>{text}</div>
											</OutboundTextMessageBubble>
										</div>

										<div
											className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.mb2)}
											role='group'
											aria-label='Draft information'
										>
											<span
												className={css(bs.textXs, bs.textGray400, bs.px2, bs.py1, bs.bgGray200, bs.rounded)}
												role='status'
											>
												Draft
											</span>
											{lastModified && (
												<time className={css(bs.textXs, bs.textGray400)} dateTime={template.lastModifiedDate}>
													Modified {lastModified}
												</time>
											)}
										</div>

										<button
											className={css(bs.ctaButtonReverseSmall, bs.bgTransparent)}
											onClick={() => editDraft(template)}
											aria-label={`Edit draft campaign: ${template.name}`}
										>
											Edit Draft
										</button>
									</div>
								);
							} catch {
								return null;
							}
						})
						.filter(Boolean); // Remove any null entries from error cases
				})}
				{draftsQuery.isFetchingNextPage ? (
					<div className={css(bs.wFull, bs.flex, bs.justifyCenter)}>
						<LoadingSpinner className={css()} type='small' data-testid='loading-spinner' />
					</div>
				) : null}
				<Waypoint
					bottomOffset='-200px'
					onEnter={() => {
						if (draftsQuery.hasNextPage && !draftsQuery.isFetchingNextPage) {
							draftsQuery.fetchNextPage();
						}
					}}
				/>

				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={{
						isOpen: !!draftToDelete,
						onRequestClose: onDeleteConfirmationRequestClose,
					}}
					options={[
						{
							isDestructive: true,
							representedObject: true,
							title: 'Delete Draft',
						},
						{
							isCancel: true,
							representedObject: false,
							title: 'Cancel',
						},
					]}
					title={<span>{`Are you sure you want to delete the "${draftToDelete?.name || ''}" draft?`}</span>}
				/>
			</div>
		</DraftErrorBoundary>
	);
});

TextingCampaignDraftBrowserCards.displayName = 'TextingCampaignDraftBrowserCards';
